import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { getLocale } from "react-i18nify";
import { useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
// import dayjs from "dayjs";

import { Avatar, FormControlLabel, Grid, Stack } from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";

import EditIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CameraAltIcon from "@mui/icons-material/CameraAlt";

import {
  getAdminDetail,
  getAllAdminRole,
  updateAdminDetail,
} from "src/api/admin";
import { recoverPassword } from "src/api/auth";

import font from "src/styles/font";
import { device } from "src/styles/device";
import { Black, GreyStroke, White, White01 } from "src/styles/color";
import { checkNotEmpty } from "src/common";
import { useAuth } from "src/hooks/useAuth";
import { useDialog } from "src/context/DialogContext";

import Forbidden from "src/pages/Forbidden";
import Main from "src/components/ui/Container";
import Loading from "src/components/ui/Loading";
import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";
import Checkbox from "src/components/core/Checkbox";
import UploadImage from "src/components/upload/dropzone/UploadImage";

const Container = styled.div`
  width: 100%;
  max-width: 1240px;
  position: relative;
  padding: 24px 40px 40px;
  margin: 0 auto;
  /* background-color: ${White01}; */

  & > div > p {
    word-wrap: break-word;
  }

  @media ${device.tabletL} {
    padding: 24px 40px 40px;
  }
  @media ${device.mobileL} {
    padding: 20px;
  }
`;

const DivBackground = styled.div`
  width: 100%;
  height: ${({ fullHeight }) => (fullHeight ? "100%" : "auto")};
  padding: 24px;
  background-color: #ffffff;
  /* box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.2); */
  border-radius: 16px;
  /* margin: 16px 0; */

  @media ${device.tabletL} {
    padding: 16px;
  }
`;

const SpanTitle = styled.div`
  color: ${({ color }) => (color ? color : Black)};
  font-size: ${font.size.lg};
  font-weight: 600;
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : 0)};

  @media ${device.tabletL} {
    font-size: ${font.size.md};
  }
`;

const SpanHeader = styled.p`
  color: ${Black};
  font-size: ${font.size.sm};
  font-weight: 500;
  /* line-height: 24px; */
  margin: 0;

  &:not(:last-child) {
    margin-bottom: 8px;
  }

  @media ${device.tabletL} {
    font-size: ${font.size.xs};
  }
`;

const SpanSubHeader = styled.p`
  color: ${Black};
  font-size: ${font.size.sm};
  font-weight: 400;
  /* line-height: 24px; */
  margin: 0;

  &:not(:last-child) {
    margin-bottom: 8px;
  }

  span {
    color: ${Black};
    font-size: ${font.size.sm};
    font-weight: 500;
  }

  @media ${device.tabletL} {
    font-size: ${font.size.xs};

    span {
      font-size: ${font.size.xs};
    }
  }
`;

const SpanContent = styled.div`
  min-height: 48px;
  color: ${Black};
  font-size: ${font.size.md};
  font-weight: 400;
  line-height: 24px;
  margin: 0;
  background: ${White01};
  padding: 12px 16px;
  border-radius: 16px;

  > a {
    color: ${Black};
    font-size: ${font.size.md};
    font-weight: 400;
    text-decoration: underline;
  }

  @media ${device.tabletL} {
    padding: 8px 12px;
    font-size: ${font.size.sm};
    min-height: 40px;
  }
`;

const WrapperUploadAvatar = styled.div`
  width: 120px;
  height: 120px;
  position: relative;
  overflow: hidden;
  border-radius: 50%;
  cursor: pointer;

  @media ${device.tabletL} {
    width: 100px;
    height: 100px;
  }
`;

const UploadAvatarText = styled.div`
  width: 100%;
  position: absolute;
  bottom: 0;
  background-color: #252525b5;
  padding: 6px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Accordion = styled(MuiAccordion)`
  border: 1px solid ${GreyStroke};

  &.MuiAccordion-root:first-of-type,
  &.MuiAccordion-root:first-of-type > .MuiAccordionSummary-root {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }
  &.MuiAccordion-root:last-of-type,
  &.MuiAccordion-root:last-of-type > .MuiAccordionSummary-root {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;

const AccordionSummary = styled(MuiAccordionSummary)`
  &.MuiAccordionSummary-root {
    background: ${White01};
  }
  &.MuiAccordionSummary-root.Mui-expanded {
    min-height: 56px;
  }
  & .MuiAccordionSummary-content.Mui-expanded {
    margin: 12px 0;
  }
`;

const AccordionDetails = styled(MuiAccordionDetails)`
  border-top: 1px solid ${GreyStroke};

  &.MuiAccordionDetails-root {
    padding: 16px;
  }
`;

const HeaderLabel = styled.div`
  color: ${Black};
  font-size: ${font.size.md};
  font-weight: 500;
  line-height: 24px;
  /* text-align: center;
  margin: auto; */

  @media ${device.tabletL} {
    font-size: ${font.size.sm};
  }
`;

const AdminDetail = ({ open }) => {
  const { user, me, checkAllowPermission } = useAuth();
  let { userId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { openDialog, closeDialog, closeAllDialogs } = useDialog();

  const defaultPermission = {
    role: "",
    permission: {
      admins: {
        can_view: false,
        can_create: false,
        can_edit: false,
        can_delete: false,
      },
      users: {
        can_view: false,
        can_edit: false,
        // can_delete: false,
      },
      sellers: {
        can_view: false,
        can_edit: false,
        can_approve: false,
        can_setting: false,
      },
      dashboards: {
        can_view: false,
      },
      products: {
        can_view: false,
        can_create: false,
        can_edit: false,
        can_approve: false,
        can_hold_delete: false,
        can_public: false,
        can_update_stock: false,
        can_view_costPrice: false,
        // can_view_specialPrice: false,
      },
      reviews: {
        can_view: false,
        can_create: false,
        can_edit: false,
        can_reply: false,
        can_approve: false,
        can_public: false,
      },
      quotations: {
        can_view: false,
        can_create: false,
        can_replace: false,
        can_approve: false,
      },
      certificates: {
        can_view: false,
        can_create: false,
        can_edit: false,
      },
      orders: {
        can_view_sampleRequest: false,
        can_approve_sampleRequest: false,
        can_view_purchaseOrder: false,
        can_update_purchaseOrder: false,
        can_view_shippingOrder: false,
        can_update_shippingOrder: false,
        can_view_cancelOrder: false,
        can_update_cancelOrder: false,
      },
      promo_codes: {
        can_view: false,
        can_create: false,
        can_edit: false,
        can_delete: false,
      },
      sale_features: {
        can_view_adminQuotation: false,
        can_view_saleQuotation: false,
        can_create_saleQuotation: false,
        can_view_saleOrder: false,
        can_create_saleOrder: false,
      },
      features: {
        can_manage_landingPage: false,
        can_manage_xclusiveDeal: false,
        can_manage_shopThisMood: false,
        can_manage_blog: false,
        can_manage_productType: false,
      },
      collections: {
        can_view: false,
        can_create: false,
        can_edit: false,
        can_delete: false,
      },
      wallplast: {
        can_view: false,
      },
    },
  };
  const [adminDetail, setAdminData] = useState({
    admin_permission: defaultPermission,
  });
  const [updateDetail, setUpdateDetail] = useState({
    admin_permission: defaultPermission,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isEditProfile, setIsEditProfile] = useState(false);

  const permissionMenu = [
    {
      label: "Dashboard",
      key: "dashboards",
      subList: [
        {
          label: "สิทธิ์การเข้าถึงการมองเห็น",
          key: "can_view",
        },
      ],
    },
    {
      label: "รายการสินค้า (Product)",
      key: "products",
      subList: [
        {
          label: "สิทธิ์การเข้าถึงการมองเห็น",
          key: "can_view",
        },
        {
          label: "อัพโหลดสินค้า",
          key: "can_create",
        },
        {
          label: "แก้ไขสินค้า",
          key: "can_edit",
        },
        {
          label: "อนุมัติสินค้า",
          key: "can_approve",
        },
        {
          label: "ลบสินค้า/พักการขาย",
          key: "can_hold_delete",
        },
        {
          label: "เปิด/ปิดการมองเห็นสินค้า",
          key: "can_public",
        },
        {
          label: "แก้ไขสต็อกสินค้า",
          key: "can_update_stock",
        },
        {
          label: "สามารถเห็นราคาต้นทุน",
          key: "can_view_costPrice",
        },
        // {
        //   label: "สามารถเห็นราคาพิเศษ",
        //   key: "can_view_specialPrice",
        // },
      ],
    },

    {
      label: "ใบรับรอง (Certificate)",
      key: "certificates",
      subList: [
        {
          label: "สิทธิ์การเข้าถึงการมองเห็น",
          key: "can_view",
        },
        {
          label: "สร้างใบรับรอง",
          key: "can_create",
        },
        {
          label: "แก้ไขใบรับรอง",
          key: "can_edit",
        },
      ],
    },
    {
      label: "รีวิวสินค้า (Review)",
      key: "reviews",
      subList: [
        {
          label: "สิทธิ์การเข้าถึงการมองเห็น",
          key: "can_view",
        },
        {
          label: "สร้างรีวิวสินค้า",
          key: "can_create",
        },
        {
          label: "แก้ไขรีวิว",
          key: "can_edit",
        },
        {
          label: "ตอบการรีวิว",
          key: "can_reply",
        },
        {
          label: "อนุมัติรีวิว",
          key: "can_approve",
        },
        {
          label: "เปิด/ปิดการมองเห็นรีวิว",
          key: "can_public",
        },
      ],
    },
    {
      label: "ผู้ดูแลระบบ (Admin)",
      key: "admins",
      subList: [
        {
          label: "สิทธิ์การเข้าถึงการมองเห็น",
          key: "can_view",
        },
        {
          label: "เพิ่มผู้ดูแลระบบ",
          key: "can_create",
        },
        {
          label: "แก้ไขสิทธิ์ผู้ดูแลระบบ",
          key: "can_edit",
        },
        {
          label: "ลบผู้ดูแลระบบ",
          key: "can_delete",
        },
      ],
    },
    {
      label: "ลูกค้า (User)",
      key: "users",
      subList: [
        {
          label: "สิทธิ์การเข้าถึงการมองเห็น",
          key: "can_view",
        },
        // {
        //   label: "Edit User",
        //   key: "can_edit",
        // },
      ],
    },
    {
      label: "ผู้ขาย (Seller)",
      key: "sellers",
      subList: [
        {
          label: "สิทธิ์การเข้าถึงการมองเห็น",
          key: "can_view",
        },
        {
          label: "แก้ไขรายละเอียดร้านค้า",
          key: "can_edit",
        },
        {
          label: "อนุมัติร้านค้า",
          key: "can_approve",
        },
        {
          label: "ตั้งค่าร้านค้า",
          key: "can_setting",
        },
      ],
    },
    {
      label: "ใบเสนอราคา (Quotation)",
      key: "quotations",
      subList: [
        {
          label: "สิทธิ์การเข้าถึงการมองเห็น",
          key: "can_view",
        },
        {
          label: "สร้างใบเสนอราคา",
          key: "can_create",
        },
        {
          label: "สร้างใบเสนอราคาใหม่จากของเดิม",
          key: "can_replace",
        },
        {
          label: "อนุมัติใบเสนอราคา",
          key: "can_approve",
        },
      ],
    },
    {
      label: "การขอสินค้าตัวอย่าง (Sample Request)",
      key: "orders",
      subList: [
        {
          label: "สิทธิ์การเข้าถึงการมองเห็น",
          key: "can_view_sampleRequest",
        },
        {
          label: "อนุมัติการขอสินค้าตัวอย่าง",
          key: "can_approve_sampleRequest",
        },
      ],
    },
    {
      label: "รายการสั่งซื้อ (Purchase Orders)",
      key: "orders",
      subList: [
        {
          label: "สิทธิ์การเข้าถึงการมองเห็น",
          key: "can_view_purchaseOrder",
        },
        {
          label: "อัพเดตสถานะคำสั่งซื้อ",
          key: "can_update_purchaseOrder",
        },
      ],
    },
    {
      label: "รายการจัดส่ง (Shipping Orders)",
      key: "orders",
      subList: [
        {
          label: "สิทธิ์การเข้าถึงการมองเห็น",
          key: "can_view_shippingOrder",
        },
        {
          label: "อัพเดตสถานะการจัดส่ง",
          key: "can_update_shippingOrder",
        },
      ],
    },
    {
      label: "รายการยกเลิก/คืนเงิน (Cancel Orders)",
      key: "orders",
      subList: [
        {
          label: "สิทธิ์การเข้าถึงการมองเห็น",
          key: "can_view_cancelOrder",
        },
        {
          label: "อัพเดตสถานะการยกเลิก/คืนเงิน",
          key: "can_update_cancelOrder",
        },
      ],
    },
    {
      label: "โค๊ดส่วนลด (Promo code)",
      key: "promo_codes",
      subList: [
        {
          label: "สิทธิ์การเข้าถึงการมองเห็น",
          key: "can_view",
        },
        {
          label: "สร้างโค๊ดส่วนลด",
          key: "can_create",
        },
        {
          label: "แก้ไขโค๊ดส่วนลด",
          key: "can_edit",
        },
        {
          label: "ลบโค๊ดส่วนลด",
          key: "can_delete",
        },
      ],
    },
    {
      label: "ใบเสนอคาราคาชั่วคราว (Sale Quotation)",
      key: "sale_features",
      subList: [
        // {
        //   label: "สิทธิ์การเข้าถึงการมองเห็นเมนูแอดมิน",
        //   key: "can_view_adminQuotation",
        // },
        {
          label: "สิทธิ์การเข้าถึงการมองเห็น",
          key: "can_view_saleQuotation",
        },
        {
          label: "สร้างใบเสนอราคาชั่วคราว",
          key: "can_create_saleQuotation",
        },
      ],
    },
    {
      label: "สร้างคำสั่งซื้อ (Sale Order)",
      key: "sale_features",
      subList: [
        {
          label: "สิทธิ์การเข้าถึงการมองเห็น",
          key: "can_view_saleOrder",
        },
        {
          label: "สร้างคำสั่งซื้อ",
          key: "can_create_saleOrder",
        },
      ],
    },
    {
      label: "เครื่องมือทางการตลาด (Features)",
      key: "features",
      subList: [
        {
          label: "สิทธิ์การจัดการ Landing Page",
          key: "can_manage_landingPage",
        },
        {
          label: "สิทธิ์การจัดการ Xclusive Deal",
          key: "can_manage_xclusiveDeal",
        },
        {
          label: "สิทธิ์การจัดการ Shop this mood",
          key: "can_manage_shopThisMood",
        },
        {
          label: "สิทธิ์การจัดการบทความ",
          key: "can_manage_blog",
        },
        {
          label: "สิทธิ์การจัดการประเภทสินค้า",
          key: "can_manage_productType",
        },
      ],
    },
    {
      label: "คอลเลคชัน (Collection)",
      key: "collections",
      subList: [
        {
          label: "สิทธิ์การเข้าถึงการมองเห็น",
          key: "can_view",
        },
        {
          label: "สร้างคอลเลคชัน",
          key: "can_create",
        },
        {
          label: "แก้ไขคอลเลคชัน",
          key: "can_edit",
        },
        {
          label: "ลบคอลเลคชัน",
          key: "can_delete",
        },
      ],
    },
    {
      label: "Wallplast",
      key: "wallplast",
      subList: [
        {
          label: "สิทธิ์การเข้าถึงการมองเห็น",
          key: "can_view",
        },
      ],
    },
  ];
  const [adminRoleList, setAdminRoleList] = useState([]);

  const isFormValid = [
    "first_name",
    "last_name",
    "email",
    "admin_permission",
  ].every((required) => {
    let objVal = updateDetail[required];

    if (required === "admin_permission")
      return Object.keys(objVal).every((req) => {
        let subObj = objVal[req];
        return checkNotEmpty(subObj);
      });

    return checkNotEmpty(objVal);
  });

  const fetchAdminDetail = async () => {
    try {
      setIsLoading(true);
      const data = await getAdminDetail(userId);
      // console.log("-----data------", data);
      setAdminData({
        ...data,
        name:
          data.register_type === 1 // email-pwd
            ? checkNotEmpty(data?.first_name) && checkNotEmpty(data?.last_name)
              ? `${data?.first_name} ${data?.last_name}`
              : "-"
            : data.register_type === 2 // facebook
              ? data.facebook_name
              : data.register_type === 3 // google
                ? data.google_name
                : "-",
        admin_permission: data?.admin_permission || defaultPermission,
        // role: data?.admin_permission?.role || "",
      });
      setUpdateDetail({
        ...data,
        admin_permission: data?.admin_permission || defaultPermission,
        // role: data?.admin_permission?.role || "",
      });
    } catch (err) {
      console.error("detail err:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllRole = async () => {
    try {
      setIsLoading(true);
      const data = await getAllAdminRole();
      setAdminRoleList(data);
    } catch (err) {
      console.error("role err:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecoverPassword = async () => {
    try {
      setIsLoading(true);
      const res = await recoverPassword({ email: adminDetail.email });

      if (!!res) {
        enqueueSnackbar(
          `${getLocale() === "th" ? res?.message?.th : res?.message?.en}`,
          {
            variant: "success",
            autoHideDuration: 3000,
          },
        );
      }
    } catch (err) {
      console.error("err:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectRole = async (value) => {
    setUpdateDetail((prev) => ({
      ...prev,
      admin_permission: {
        ...prev.admin_permission,
        role: value,
        permission: value.default_permission,
      },
    }));
  };

  const handleChangePermission = async (key, subKey, value) => {
    setUpdateDetail((prev) => ({
      ...prev,
      admin_permission: {
        ...prev.admin_permission,
        permission: {
          ...prev.admin_permission?.permission,
          [key]: {
            ...prev.admin_permission?.permission?.[key],
            [subKey]: value,
          },
        },
      },
    }));
  };

  const handleUpdate = async () => {
    try {
      setIsLoading(true);

      const updateParams = {
        ...updateDetail,
        admin_permission: JSON.stringify({
          ...updateDetail?.admin_permission,
          role: updateDetail?.admin_permission?.role?._id,
        }),
      };

      const res = await updateAdminDetail(userId, updateParams);

      enqueueSnackbar(res?.message, {
        variant: "success",
        autoHideDuration: 3000,
      });
      // setIsEditProfile(false);

      await fetchAdminDetail();
      await me();
      setIsEditProfile(false);
    } catch (err) {
      console.error("update err:", err);
    } finally {
      setIsLoading(false);
      closeAllDialogs();
    }
  };

  useEffect(() => {
    if (userId === undefined) return;

    fetchAdminDetail();
    fetchAllRole();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    limit: 100,
  });

  if (user?.role_id === 3) return <Forbidden />;

  return (
    <>
      <Main open={open}>
        <Container>
          <Grid container spacing={2}>
            {/* Card */}
            <Grid item xs={12} md={4}>
              <DivBackground fullHeight>
                <Grid container spacing={2}>
                  <Grid item xs="auto" md={12}>
                    {isEditProfile ? (
                      <>
                        <UploadImage
                          callBackFile={(fileCallBlack) => {
                            const dataUp = updateDetail;
                            setUpdateDetail({
                              ...dataUp,
                              user_profile_image: fileCallBlack,
                            });
                          }}
                          closeAlert={false}
                        >
                          <WrapperUploadAvatar>
                            <Avatar
                              src={
                                typeof updateDetail?.user_profile_image ===
                                "object"
                                  ? updateDetail?.user_profile_image?.fileData
                                  : updateDetail?.user_profile_image
                              }
                              sx={{
                                width: {
                                  xs: 100,
                                  md: 120,
                                },
                                height: {
                                  xs: 100,
                                  md: 120,
                                },
                              }}
                            />
                            <UploadAvatarText>
                              <CameraAltIcon
                                sx={{
                                  color: White,
                                  fontSize: {
                                    xs: font.size.md,
                                    md: font.size.lg,
                                  },
                                }}
                              />
                            </UploadAvatarText>
                          </WrapperUploadAvatar>
                        </UploadImage>
                      </>
                    ) : (
                      <Avatar
                        src={adminDetail?.user_profile_image}
                        sx={{
                          width: {
                            xs: 100,
                            md: 120,
                          },
                          height: {
                            xs: 100,
                            md: 120,
                          },
                        }}
                      />
                    )}
                  </Grid>

                  <Grid
                    item
                    xs
                    // sm={6}
                    md={12}
                    sx={{ alignContent: { xs: "center", md: "unset" } }}
                  >
                    <SpanTitle mb={8}>{adminDetail?.name}</SpanTitle>

                    <SpanSubHeader>
                      {checkNotEmpty(adminDetail?.email)
                        ? adminDetail?.email
                        : "-"}
                    </SpanSubHeader>
                  </Grid>

                  {/* <Grid item xs={12} order={{ xs: 3, md: 2 }}>
                    <Divider sx={{ mb: 2 }} />

                    <WrapperSubHeader>
                      <SpanSubHeader>
                        <span>
                          {getLocale() === "th"
                            ? "วันที่เข้าร่วม"
                            : "Joined Date"}
                          :{" "}
                        </span>
                        {checkNotEmpty(adminDetail?.createdAt)
                          ? dayjs(adminDetail?.createdAt).format("L")
                          : "-"}
                      </SpanSubHeader>

                      <SpanSubHeader>
                        <span>
                          {getLocale() === "th" ? "เข้าร่วมผ่าน" : "Joined By"}:{" "}
                        </span>
                        {adminDetail?.register_type === 1
                          ? getLocale() === "th"
                            ? "Xsurface"
                            : "Xsurface"
                          : adminDetail?.register_type === 2
                            ? getLocale() === "th"
                              ? "Facebook"
                              : "Facebook"
                            : adminDetail?.register_type === 3
                              ? getLocale() === "th"
                                ? "Google"
                                : "Google"
                              : "-"}
                      </SpanSubHeader>
                    </WrapperSubHeader>
                  </Grid> */}

                  <Grid
                    item
                    xs={6}
                    sm="auto"
                    md={12}
                    sx={{ alignContent: { xs: "center", md: "unset" } }}
                  >
                    <Button
                      onClick={() =>
                        openDialog({
                          type: "confirm",
                          title: `การรีเซ็ตรหัสผ่านจะส่งไปยังอีเมลที่ระบุไว้\nคุณต้องการรีเซ็ตรหัสผ่านใช่หรือไม่`,
                          onConfirm: () => {
                            handleRecoverPassword();
                          },
                          maxWidth: 400,
                        })
                      }
                      sx={{
                        width: "100%",
                        maxWidth: {
                          xs: 150,
                          md: "unset",
                        },
                      }}
                      variant="outlined"
                      color="secondary"
                    >
                      {getLocale() === "th"
                        ? "รีเซ็ตรหัสผ่าน"
                        : "Reset Password"}
                    </Button>
                  </Grid>
                </Grid>
              </DivBackground>
            </Grid>

            {/* Profile data */}
            <Grid item xs={12} md={8}>
              <DivBackground>
                <Grid container spacing={{ xs: 1, sm: 2 }}>
                  <Grid
                    item
                    xs={4}
                    sm={6}
                    md={4}
                    // lg={6}
                    sx={{ alignContent: "center" }}
                  >
                    <SpanTitle>
                      {getLocale() === "th"
                        ? "บัญชีโปรไฟล์"
                        : "Profile account"}
                    </SpanTitle>
                  </Grid>

                  <Grid item xs={8} sm={6} md={8} sx={{ textAlign: "right" }}>
                    {checkAllowPermission("admins.can_edit") &&
                      (isEditProfile ? (
                        <>
                          <Button
                            onClick={() =>
                              openDialog({
                                type: "confirm",
                                title: "คุณต้องการยกเลิกการแก้ไขใช่หรือไม่",
                                onConfirm: () => {
                                  setIsEditProfile(false);
                                  setUpdateDetail(adminDetail);
                                  closeDialog();
                                },
                                maxWidth: 342,
                              })
                            }
                            sx={{ width: "100%", maxWidth: 120, mr: 1 }}
                            variant="outlined"
                          >
                            ยกเลิก
                          </Button>
                          <Button
                            disabled={!isFormValid}
                            onClick={() =>
                              openDialog({
                                type: "confirm",
                                title: "คุณต้องการบันทึกการแก้ไขใช่หรือไม่",
                                onConfirm: handleUpdate,
                                maxWidth: 342,
                              })
                            }
                            sx={{ width: "100%", maxWidth: 120 }}
                            variant="contained"
                          >
                            บันทึก
                          </Button>
                        </>
                      ) : (
                        <Button
                          onClick={() => setIsEditProfile(true)}
                          sx={{ width: "100%", maxWidth: 190 }}
                          variant="outlined"
                          startIcon={<EditIcon />}
                        >
                          {getLocale() === "th"
                            ? "แก้ไขโปรไฟล์"
                            : "Edit Profile"}
                        </Button>
                      ))}
                  </Grid>

                  {/* <Grid
                    item
                    xs={8}
                    sm={6}
                    md={8}
                    lg={6}
                    sx={{ textAlign: "right" }}
                  >
                    {checkAllowPermission("admins.can_edit") && (
                      <Button
                        onClick={() => {
                          if (isEditProfile) {
                            openDialog({
                              type: "confirm",
                              title: `คุณต้องการยกเลิกการแก้ไขใช่หรือไม่`,
                              onConfirm: () => {
                                setIsEditProfile(false);
                                setUpdateDetail(adminDetail);
                                closeDialog();
                              },
                              maxWidth: 342,
                            });
                          } else {
                            setIsEditProfile(true);
                          }
                        }}
                        sx={{
                          width: "100%",
                          maxWidth: {
                            xs: 150,
                            md: 190,
                          },
                        }}
                        variant="outlined"
                        startIcon={<EditIcon />}
                      >
                        {getLocale() === "th" ? "แก้ไขโปรไฟล์" : "Edit Profile"}
                      </Button>
                    )}
                  </Grid> */}

                  {isEditProfile ? (
                    <>
                      <Grid item xs={12} md={6}>
                        <SpanHeader>
                          {getLocale() === "th" ? "ชื่อ" : "First name"}
                        </SpanHeader>
                        <TextField
                          variant="outlined"
                          fullWidth
                          onChange={(e) => {
                            setUpdateDetail({
                              ...updateDetail,
                              first_name: e.target.value,
                            });
                          }}
                          value={updateDetail.first_name}
                          error={!checkNotEmpty(updateDetail.first_name)}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <SpanHeader>
                          {getLocale() === "th" ? "นามสกุล" : "Last name"}
                        </SpanHeader>
                        <TextField
                          variant="outlined"
                          fullWidth
                          onChange={(e) => {
                            setUpdateDetail({
                              ...updateDetail,
                              last_name: e.target.value,
                            });
                          }}
                          value={updateDetail.last_name}
                          error={!checkNotEmpty(updateDetail.last_name)}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <SpanHeader>Email</SpanHeader>
                        <TextField
                          variant="outlined"
                          fullWidth
                          onChange={(e) => {
                            setUpdateDetail({
                              ...updateDetail,
                              email: e.target.value,
                            });
                          }}
                          disabled
                          value={updateDetail.email}
                          error={!checkNotEmpty(updateDetail.email)}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <SpanHeader>Role</SpanHeader>
                        <Autocomplete
                          aria-required={true}
                          id="role-select"
                          isOptionEqualToValue={(option, value) =>
                            option?._id === value?._id
                          }
                          disableClearable
                          options={adminRoleList}
                          value={updateDetail?.admin_permission?.role || ""}
                          onChange={(event, newValue) => {
                            handleSelectRole(newValue);
                          }}
                          getOptionLabel={(option) => {
                            return option?.role_name || "";
                          }}
                          filterOptions={filterOptions}
                          fullWidth
                          sx={{
                            "&.MuiAutocomplete-root .MuiOutlinedInput-root": {
                              padding: "12px 16px",
                            },
                            ".MuiInputBase-input.MuiOutlinedInput-input": {
                              padding: 0,
                            },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="เลือก Role"
                              error={
                                !checkNotEmpty(
                                  updateDetail?.admin_permission?.role,
                                )
                              }
                            />
                          )}
                        />
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={12} md={6}>
                        <SpanHeader>
                          {getLocale() === "th" ? "ชื่อ" : "First name"}
                        </SpanHeader>
                        <SpanContent>{adminDetail?.first_name}</SpanContent>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <SpanHeader>
                          {getLocale() === "th" ? "นามสกุล" : "Last name"}
                        </SpanHeader>
                        <SpanContent>{adminDetail?.last_name}</SpanContent>
                      </Grid>

                      <Grid item xs={12}>
                        <SpanHeader>Email</SpanHeader>
                        <SpanContent>
                          {checkNotEmpty(adminDetail?.email)
                            ? adminDetail?.email
                            : "-"}
                        </SpanContent>
                      </Grid>

                      <Grid item xs={12}>
                        <SpanHeader>Role</SpanHeader>
                        <SpanContent>
                          {adminDetail?.admin_permission?.role?.role_name ||
                            "-"}
                        </SpanContent>
                      </Grid>
                    </>
                  )}
                </Grid>
              </DivBackground>
            </Grid>

            {/* Permission */}
            <Grid item xs={12}>
              <DivBackground>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <SpanTitle>
                      {getLocale() === "th"
                        ? "กำหนดสิทธิ์ผู้ดูแลระบบ"
                        : "Admin Permission"}
                    </SpanTitle>
                  </Grid>

                  <Grid item xs={12}>
                    {permissionMenu?.map(({ key, ...menu }, index) => (
                      <Accordion key={`menu-${key}-${index}`} defaultExpanded>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls={`panel-${index}-content`}
                          id={`panel-${index}-header`}
                        >
                          <HeaderLabel>{menu.label}</HeaderLabel>
                        </AccordionSummary>

                        <AccordionDetails>
                          <Grid container spacing={2}>
                            {menu.subList.map(({ key: subKey, ...action }) => (
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                md={4}
                                key={`${key}-action-${subKey}`}
                              >
                                <FormControlLabel
                                  label={action.label}
                                  disabled={!isEditProfile}
                                  sx={{
                                    margin: 0,
                                    ".MuiFormControlLabel-label": {
                                      fontSize: "1rem",
                                      fontWeight: 400,
                                      lineHeight: "24px",
                                      color: Black,
                                    },
                                    ".MuiCheckbox-root": { p: 0.5, mr: 0.5 },
                                    ".MuiFormControlLabel-label.Mui-disabled": {
                                      color: Black,
                                    },
                                  }}
                                  control={
                                    <Checkbox
                                      name={`action-${subKey}-checkbox`}
                                      checked={
                                        updateDetail.admin_permission
                                          .permission?.[key]?.[subKey] || false
                                      }
                                      onChange={(e) =>
                                        handleChangePermission(
                                          key,
                                          subKey,
                                          e.target.checked,
                                        )
                                      }
                                    />
                                  }
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Grid>

                  {isEditProfile && (
                    <Grid item xs={12}>
                      <Stack
                        flexDirection={"row"}
                        alignItems={"center"}
                        justifyContent={"center"}
                        gap={2}
                      >
                        <Button
                          variant="outlined"
                          onClick={() => {
                            openDialog({
                              type: "confirm",
                              title: `คุณต้องการยกเลิกการแก้ไขใช่หรือไม่`,
                              onConfirm: () => {
                                setIsEditProfile(false);
                                setUpdateDetail(adminDetail);
                                closeDialog();
                              },
                              maxWidth: 342,
                            });
                          }}
                          sx={{ minWidth: 200 }}
                        >
                          ยกเลิก
                        </Button>
                        <Button
                          variant="contained"
                          disabled={!isFormValid}
                          onClick={() =>
                            openDialog({
                              type: "confirm",
                              title: `คุณต้องการบันทึกการแก้ไขใช่หรือไม่`,
                              onConfirm: () => handleUpdate(),
                              maxWidth: 342,
                            })
                          }
                          sx={{ minWidth: 200 }}
                        >
                          บันทึก
                        </Button>
                      </Stack>
                    </Grid>
                  )}
                </Grid>
              </DivBackground>
            </Grid>
          </Grid>
        </Container>
      </Main>

      {isLoading && <Loading />}
    </>
  );
};
export default AdminDetail;
